import * as React from 'react';
import { useRef, useState } from "react";
import { useForm, type FieldError } from 'react-hook-form';
import HookFormDevToolLazy from "../../js/components/hookFormDevToolLazy.tsx";
import Spinner from "../../js/components/spinner.tsx";
import { clsx } from 'clsx';
import { getEnv } from "../../js/helpers/env.ts";

interface CallbackFormValues {
	name: string,
	email: string,
	phone: string,
	preferred: string,
	consent: boolean,
}

type Props = Readonly<{}>

export default function CallbackForm({...otherProps}: Props) {
	const [isSent, setIsSent] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		control,
	} = useForm<CallbackFormValues>();

	const form = useRef(null);

	function inputRadioClass(error: boolean|string|FieldError = false) {
		return clsx('border rounded', (error ? '!border-red-400' : 'border-transparent'));
	}

	function inputClass(error: boolean|string|FieldError = false) {
		return clsx('border text-black', error && '!border-red-400');
	}

	return (<>
		<form ref={form} onSubmit={handleSubmit(onSubmit)} className="relative">
			<HookFormDevToolLazy control={control}/>

			<div className="flex flex-wrap overflow-hidden max-w-screen-2xl -mx-12 text-lg pb-3">
				<div className="w-full px-12 mb-4">
					<label className="mb-2 inline-block" htmlFor="name">NAME *</label><br/>
					<input
						//defaultValue="Hallo"
						className={clsx('w-full border-grey-300', inputClass(errors.name))}
						type="text"
						id="name" name="name"
						size={20}
						{...register("name", {required: true})}
					/>
				</div>

				<div className="w-full px-12 mb-4">
					<label className="mb-2 inline-block" htmlFor="phone">PHONE *</label><br/>
					<input
						//defaultValue="789789789"
						className={clsx('w-full border-grey-300', inputClass(errors.phone))}
						type="tel"
						id="phone" name="phone"
						size={20}
						{...register("phone", {required: true, maxLength: 12})}
					/>
				</div>

				<div className="w-full px-12 mb-4">
					<label className="mb-2 inline-block" htmlFor="preferred">PREFERRED TIME TO CALL (Mon-Fri) </label><br/>
					<select
						//defaultValue="789789789"
						className={clsx('w-full border-grey-300', inputClass(errors.preferred))}
						id="preferred" name="preferred"
						{...register("preferred", {required: false})}
					>
						<option value="">– Please choose an option –</option>
						<option value="9:00-12:00">9:00-12:00</option>
						<option value="12:00-2:00">12:00-2:00</option>
						<option value="2:00-5:00">2:00-5:00</option>
					</select>
				</div>

				<div className="w-full px-12 mb-4">
					<label className="mb-2 inline-block" htmlFor="email">EMAIL</label><br/>
					<input
						//defaultValue="tst@gfdgsd.pl"
						className={clsx('w-full border-grey-300', inputClass(errors.email))}
						type="email"
						id="email" name="email"
						size={20}
						{...register("email", {required: false, pattern: /^\S+@\S+$/i})}
					/>
				</div>

				<div className="w-full px-12 mb-4">
					<div className={clsx('border px-4 -mx-4 flex py-1 align-middle', inputRadioClass(errors.consent))}>
						<input
							className="!mb-0 !focus:outline-0"
							id="consent" name="consent"
							type="checkbox"
							//checked
							{...register("consent", {required: true})}
						/>
						<label className="mb-0 text-base inline-block ml-2" htmlFor="consent">
							I consent to <>{getEnv('PUBLIC_COMPANY_NAME')}</> collecting this information.
							For further details please visit our
							<a href="/privacy-policy" target="_blank" className="underline inline-block ml-1.5">privacy policy</a>.
						</label>
					</div>
				</div>
			</div>

			<button
				disabled={isSubmitting}
				className="w-full sm:w-auto min-w-[180px] mr-6"
				type="submit"
			>
				{isSubmitting ? 'SENDING' : 'SUBMIT'}
				{isSubmitting && <Spinner className="!w-4 !h-4"/>}
			</button>
			{isSent && <div onClick={() => setIsSent(false)}>We will respond to the inquiry as soon as possible.</div>}
		</form>
	</>);

	function resetForm() {
		reset({
			name: '',
			email: '',
			phone: '',
			consent: true,
			preferred: null,
		});
	}

	function onSubmit() {
		setIsSent(false);

		const data = new FormData(form.current);

		return fetch(getEnv('PUBLIC_API_HOST') + '/process/callback', {
			method: "POST",
			body: data
		}).then(() => {
			setIsSent(true);
			resetForm();
		});
	}
}
